// @flow
import React, { Component, Fragment } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import Banner from '../../../elements/Banner/Banner';
import EditProfileContainer from '../edit-profile/containers/EditProfileContainer';
import SecurityContainer from '../security/containers/SecurityContainer';
import PasswordContainer from '../password/containers/PasswordContainer';
import SavedAddressesContainer from '../saved-addresses/containers/SavedAddressesContainer';
import PaymentsContainer from '../payments/containers/PaymentsContainer';
import PayoutsContainer from '../payouts/containers/PayoutsContainer';
import NotificationsContainer from '../notifications/containers/NotificationsContainer';
import ReferralContainer from '../referral/containers/ReferralContainer';
import { Default, Mobile } from '../../../utils/responsive';
import Loading from '../../../elements/Loading/Loading';
import AlertContainer from '../../alert/container/AlertContainer';
import routes from '../../../routes';
import Page from '../../../Page';

import style from './Preferences.scss';
import icons from '../../../assets/styles/icons.scss';

import type { Location } from '../../../router';
import type { Address, Bank, Payment, Payout, Settings, User } from '../../../entities';

type Props = {
  +location: Location;
  +fetchNotifications: () => void;
  +isFetchingNotificationsSettings?: boolean;
  +notificationsSettings?: Settings;
  +fetchAddresses: () => void;
  +isFetchingAddresses?: boolean;
  +addresses?: Address[];
  +fetchPayouts: () => void;
  +isFetchingPayouts?: boolean;
  +payouts?: Payout;
  +fetchPayments: () => void;
  +isFetchingPayments: boolean;
  +payments: Payment [];
  +user?: User;
};

const TITLES = {
  'edit-profile': { translateId: 'preferences.menu.editProfile' },
  'security': { translateId: 'preferences.menu.security' },
  'password': { translateId: 'preferences.menu.password' },
  'saved-addresses': { translateId: 'preferences.menu.savedAddresses' },
  'payments': { translateId: 'preferences.menu.payments' },
  'payouts': { translateId: 'preferences.menu.payouts' },
  'notifications': { translateId: 'preferences.menu.notifications' },
  'undefined': { translateId: 'preferences.header.title' },
  'referral': { translateId: 'preferences.menu.referral' },
  '': { translateId: 'preferences.header.title' },
};

export default class Preferences extends Component<Props> {
  componentDidMount() {
    const {
      fetchNotifications, notificationsSettings, fetchAddresses, addresses, fetchPayouts, payouts,
      fetchPayments, payments,
    } = this.props;
    if (!notificationsSettings) fetchNotifications();
    if (!addresses) fetchAddresses();
    if (!payouts) fetchPayouts();
    // if (!payments) fetchPayments();
  }

  render() {
    const { location, isFetchingNotificationsSettings, isFetchingAddresses, isFetchingPayouts, user } = this.props;
    const subRoute = location.pathname.split('/')[2];
    const title = TITLES[subRoute] &&
      <Fragment>
        <Default>
          <FormattedMessage id={'preferences.header.title'}>{
            txt => <h1>{txt}</h1>
          }</FormattedMessage>
        </Default>
        <Mobile>
          <FormattedMessage id={TITLES[subRoute].translateId}>{
            txt => <h1>
              {subRoute && <Link to="/preferences">
                <i className={icons.iconChevronLeftLight}/>{txt}</Link>}
            </h1>
          }</FormattedMessage>
        </Mobile>
      </Fragment>;
    return [
      <Banner key="banner"
              titleElement={title}
              color="blue"/>,
      <section key="preferencesWrap"
               className={style.preferencesWrap}>
        <section className={style.preferencesContent}>
          <div className={style.greyBlock}/>
          <nav className={classNames(style.subMenu, { [style.hidden]: !!subRoute })}>
            <ul>
              <li className={classNames({ [style.active]: subRoute === 'edit-profile' })}>
                <i className={classNames(icons.iconEditMedium, style.iconList)}/>
                <FormattedMessage id="preferences.menu.editProfile">{
                  txt => (<Link to="/preferences/edit-profile">{txt}</Link>)
                }</FormattedMessage>
                <i className={icons.iconChevronRightMedium}/>
              </li>
              <li className={classNames({ [style.active]: subRoute === 'security' })}>
                <i className={classNames(icons.iconSecureCheckMedium, style.iconList)}/>
                <FormattedMessage id="preferences.menu.security">{
                  txt => (<Link to="/preferences/security">{txt}</Link>)
                }</FormattedMessage>
                <i className={icons.iconChevronRightMedium}/>
              </li>
              {user && !user.social.facebook && <li className={classNames({ [style.active]: subRoute === 'password' })}>
                <i className={classNames(icons.symbolKey, style.iconList)}/>
                <FormattedMessage id="preferences.menu.password">{
                  txt => (<Link to="/preferences/password">{txt}</Link>)
                }</FormattedMessage>
                <i className={icons.iconChevronRightMedium}/>
              </li>}
              <li className={classNames({ [style.active]: subRoute === 'saved-addresses' })}>
                <i className={classNames(icons.iconLocationSquareMedium, style.iconList)}/>
                <FormattedMessage id="preferences.menu.savedAddresses">{
                  txt => (<Link to="/preferences/saved-addresses">{txt}</Link>)
                }</FormattedMessage>
                <i className={icons.iconChevronRightMedium}/>
              </li>
              <li className={classNames({ [style.active]: subRoute === 'payouts' })}>
                <i className={classNames(icons.symbolWiredTransfer, style.iconList)}/>
                <FormattedMessage id="preferences.menu.payouts">{
                  txt => (<Link to="/preferences/payouts">{txt}</Link>)
                }</FormattedMessage>
                <i className={icons.iconChevronRightMedium}/>
              </li>
              <li className={classNames({ [style.active]: subRoute === 'notifications' })}>
                <i className={classNames(icons.iconMailMedium, style.iconList)}/>
                <FormattedMessage id="preferences.menu.notifications">{
                  txt => (<Link to="/preferences/notifications">{txt}</Link>)
                }</FormattedMessage>
                <i className={icons.iconChevronRightMedium}/>
              </li>
              <li className={classNames({ [style.active]: subRoute === 'referral' })}>
                <i className={classNames(icons.symbolGift, style.iconList)}/>
                <FormattedMessage id="preferences.menu.referral">{
                  txt => (<Link to="/preferences/referral">{txt}</Link>)
                }</FormattedMessage>
                <i className={icons.iconChevronRightMedium}/>
              </li>
            </ul>
          </nav>
          <article className={style.contentWrap}>
            <AlertContainer showId="preferences"/>
            <Switch>
              <Route path={routes.PREFERENCES.children.EDIT_PROFILE.routePath}
                     render={(props: any) => !user ?
                       <Loading/> : <Page
                         name={routes.PREFERENCES.children.EDIT_PROFILE.name}>
                         <EditProfileContainer {...props}/>
                       </Page>}/>
              <Route path={routes.PREFERENCES.children.SECURITY.routePath}
                     render={(props: any) => !user ?
                       <Loading/> :
                       <Page name={routes.PREFERENCES.children.SECURITY.name}>
                         <SecurityContainer {...props}/>
                       </Page>}/>
              {/*<Route path="/preferences/password" component={PasswordContainer}/>*/}
              <Route path={routes.PREFERENCES.children.PASSWORD.routePath}
                     render={(props: any) => {
                       if (!user) {
                         return <Loading/>;
                       } else if (user.social.facebook) {
                         return <Redirect to={{
                           pathname: '/preferences/edit-profile',
                           state: { from: location }
                         }}/>
                       } else {
                         return <Page name={routes.PREFERENCES.children.PASSWORD.name}>
                           <PasswordContainer {...props}/>
                         </Page>;
                       }
                     }}/>
              <Route path={routes.PREFERENCES.children.SAVED_ADDRESSES.routePath}
                     render={(props: any) => isFetchingAddresses ?
                       <Loading/> : <Page name={routes.PREFERENCES.children.SAVED_ADDRESSES.name}>
                         <SavedAddressesContainer {...props}/>
                       </Page>}/>
              <Route path={routes.PREFERENCES.children.PAYOUTS.routePath}
                     render={(props: any) => (isFetchingPayouts) ?
                       <Loading/> :
                       <Page name={routes.PREFERENCES.children.PAYOUTS.name}>
                         <PayoutsContainer {...props}/>
                       </Page>}/>
              <Route path={routes.PREFERENCES.children.NOTIFICATIONS.routePath}
                     render={(props: any) => isFetchingNotificationsSettings ?
                       <Loading/> :
                       <Page name={routes.PREFERENCES.children.NOTIFICATIONS.name}>
                         <NotificationsContainer {...props}/>
                       </Page>}/>
              
              <Route path={routes.PREFERENCES.children.REFERRAL.routePath}
                     render={(props: any) => isFetchingNotificationsSettings ?
                       <Loading/> :
                       <Page name={routes.PREFERENCES.children.REFERRAL.name}>
                         <ReferralContainer {...props}/>
                       </Page>}/>
              <Default>
                <Redirect from="/preferences" exact to="/preferences/edit-profile"/>
              </Default>
            </Switch>
          </article>
        </section>
      </section>,
    ]
  }
}

Preferences.propTypes = {
  location: PropTypes.object.isRequired,
  fetchNotifications: PropTypes.func.isRequired,
  isFetchingNotificationsSettings: PropTypes.bool,
  notificationsSettings: PropTypes.object,
  fetchAddresses: PropTypes.func.isRequired,
  isFetchingAddresses: PropTypes.bool,
  addresses: PropTypes.array,
  fetchPayouts: PropTypes.func.isRequired,
  isFetchingPayouts: PropTypes.bool,
  payouts: PropTypes.object,
  fetchPayments: PropTypes.func.isRequired,
  isFetchingPayments: PropTypes.bool,
  payments: PropTypes.array,
  user: PropTypes.object,
};




// WEBPACK FOOTER //
// ./src/modules/preferences/components/Preferences.jsx